import _ from 'lodash';
import { Response } from 'miragejs';
import { onInsert, onRemove, onUpdate, registerMutation } from '../collectionStore';

// Identity pages. A new screen does not need this file unless it assigns roles,
// binds a group, rotates a secret, or associates a resource to a scope.

const mirrorRoles = (stub, item, removed, api) => {
    const mirrorName = stub.mirror?.collection;
    if (!mirrorName || !item) {
        return;
    }
    const mirrored = api.collectionFor(mirrorName);
    const idField = stub.idField || 'id';
    const id = item[idField];
    const index = mirrored.items.findIndex((entry) => entry?.[idField] === id);
    if (removed) {
        if (index >= 0) {
            mirrored.items.splice(index, 1);
        }
        return;
    }
    const sample = mirrored.items.find((entry) => entry?.resourceType === item.resourceType && entry?.[idField] !== id);
    const roles = _.cloneDeep(sample?.roles || []);
    const row = {
        id,
        legacyId: id,
        name: item.name,
        resourceClass: item.resourceClass,
        resourceType: item.resourceType,
        roles: index >= 0 && mirrored.items[index].roles?.length ? mirrored.items[index].roles : roles,
    };
    if (index >= 0) {
        mirrored.items[index] = { ...mirrored.items[index], ...row };
        return;
    }
    mirrored.items.push(row);
};

const ensureTracked = (assignmentStore, userId, sourceCollection, snapshot, api) => {
    if (!assignmentStore.tracked) {
        assignmentStore.tracked = new Set();
    }
    if (assignmentStore.tracked.has(userId)) {
        return;
    }
    assignmentStore.tracked.add(userId);
    if (!snapshot || !sourceCollection) {
        return;
    }
    api.collectionFor(sourceCollection).items.forEach((item) => {
        const copy = _.cloneDeep(item);
        delete copy.userId;
        assignmentStore.items.push({
            ...copy,
            userId,
            roles: copy.roles || [],
        });
    });
};

const recordAssignment = (stub, request, user, snapshot, api) => {
    const assignment = stub.assignment;
    const idField = stub.idField || 'id';
    const userId = user?.[idField];
    const scopeId = request?.params?.[assignment?.scopeIdParam];
    const roleId = request?.params?.[assignment?.roleIdParam];
    if (!assignment?.collection || !userId || !scopeId || !roleId) {
        return;
    }
    const assignments = api.collectionFor(assignment.collection);
    ensureTracked(assignments, userId, assignment.sourceCollection, snapshot, api);
    const stored = api.collectionFor(stub.collection).items.find((item) => item?.[idField] === userId) || user;
    if (Array.isArray(stored.roles) && !stored.roles.includes(roleId)) {
        stored.roles.push(roleId);
    }
    const existing = assignments.items.find((item) => item.userId === userId && item.id === scopeId);
    if (existing) {
        if (!existing.roles.some((role) => role.id === roleId)) {
            existing.roles.push({ id: roleId });
        }
        return;
    }
    assignments.items.push({
        userId,
        id: scopeId,
        legacyId: scopeId,
        resourceClass: assignment.resourceClass || 'hierarchy',
        resourceType: assignment.resourceType,
        roles: [{ id: roleId }],
    });
};

const markTracked = (collectionName, userId, api) => {
    if (!collectionName || !userId) {
        return;
    }
    const assignments = api.collectionFor(collectionName);
    if (!assignments.tracked) {
        assignments.tracked = new Set();
    }
    assignments.tracked.add(userId);
};

const clearAssignments = (stub, userId, api) => {
    if (!stub.assignmentCollection || !userId) {
        return;
    }
    const assignments = api.collectionFor(stub.assignmentCollection);
    assignments.items = assignments.items.filter((item) => item.userId !== userId);
    assignments.tracked?.delete(userId);
};

onInsert('skipped', (stub, request, body, api) => {
    const idField = stub.idField || 'id';
    recordAssignment(stub, request, { [idField]: _.get(body, 'users[0].userId') }, true, api);
});

onInsert('matched', (stub, request, existing, api) => {
    recordAssignment(stub, request, existing, true, api);
});

onInsert('creating', (stub, request, created, api) => {
    recordAssignment(stub, request, created, false, api);
    if (stub.trackCollection) {
        created.tags = [];
        markTracked(stub.trackCollection, created[stub.idField || 'id'], api);
    }
});

onInsert('created', (stub, request, created, api) => {
    mirrorRoles(stub, created, false, api);
});

onUpdate((stub, _request, item, api) => {
    mirrorRoles(stub, item, false, api);
});

onRemove((stub, _request, removed, api) => {
    mirrorRoles(stub, removed, true, api);
    clearAssignments(stub, removed?.[stub.idField || 'id'], api);
    if (!stub.bindingCollection) {
        return;
    }
    const bindings = api.collectionFor(stub.bindingCollection);
    const groupId = removed?.[stub.idField || 'id'];
    bindings.items = bindings.items.filter((item) => item.usergroupId !== groupId);
});

registerMutation('unassign', (stub, request, _template, api) => {
    const assignment = stub.assignment || {};
    const userId = request?.params?.[stub.idParam || 'userid'];
    const scopeId = request?.params?.[assignment.scopeIdParam];
    const roleId = request?.params?.[assignment.roleIdParam];
    if (!assignment.collection || !userId || !scopeId || !roleId) {
        return new Response(204);
    }
    const assignments = api.collectionFor(assignment.collection);
    ensureTracked(assignments, userId, stub.sourceCollection, true, api);
    const index = assignments.items.findIndex((item) => item.userId === userId && item.id === scopeId);
    if (index >= 0) {
        const item = assignments.items[index];
        item.roles = (item.roles || []).filter((role) => role.id !== roleId);
        if (!item.roles.length) {
            assignments.items.splice(index, 1);
        }
    }
    if (stub.userCollection) {
        const user = api.collectionFor(stub.userCollection).items.find((item) => item?.id === userId);
        const stillUsed = assignments.items.some((item) => item.userId === userId && item.roles?.some((role) => role.id === roleId));
        if (user && Array.isArray(user.roles) && !stillUsed) {
            user.roles = user.roles.filter((id) => id !== roleId);
        }
    }
    return new Response(204);
});

registerMutation('reissue', (stub, request, _template, api) => {
    const { collection, index, id } = api.findByParam(stub, request);
    if (index === -1) {
        return new Response(404, {}, { message: 'Not found' });
    }
    const item = collection.items[index];
    if (!item.auth0Id) {
        item.auth0Id = `client-${id}`;
    }
    item.auth0ClientSecret = api.takeId('secret');
    return _.cloneDeep(item);
});

const resourceIdsFromBody = (stub, request, api) => {
    const incoming = _.get(api.readRequestBody(request), stub.itemsPath || 'Resources', []);
    return (Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean);
};

registerMutation('associate', (stub, request, _template, api) => {
    const scopeId = request?.params?.[stub.scopeIdParam];
    const tagKey = stub.tagKey;
    if (!scopeId || !tagKey) {
        return {};
    }
    const collection = api.collectionFor(stub.collection);
    resourceIdsFromBody(stub, request, api).forEach((resourceId) => {
        const item = collection.items.find((entry) => entry?.[stub.idField || 'id'] === resourceId);
        if (!item) {
            return;
        }
        if (!Array.isArray(item.tags)) {
            item.tags = [];
        }
        const alreadyAssociated = item.tags.some((tag) => tag?.[tagKey] === scopeId);
        if (!alreadyAssociated) {
            item.tags.push({ [tagKey]: scopeId });
        }
    });
    return {};
});

registerMutation('dissociate', (stub, request, _template, api) => {
    const scopeId = request?.params?.[stub.scopeIdParam];
    const resourceId = request?.params?.[stub.idParam];
    const tagKey = stub.tagKey;
    const item = api.collectionFor(stub.collection).items.find((entry) => entry?.[stub.idField || 'id'] === resourceId);
    if (item && Array.isArray(item.tags) && tagKey && scopeId) {
        item.tags = item.tags.filter((tag) => tag?.[tagKey] !== scopeId);
    }
    return new Response(204);
});

const bindingKey = (stub) => `${stub.resourceType}Id`;

registerMutation('bind', (stub, request, _template, api) => {
    const groupId = _.get(api.readRequestBody(request), 'usergroups[0].usergroupId');
    const scopeId = request?.params?.[stub.scopeIdParam];
    const roleId = request?.params?.[stub.roleIdParam];
    if (!groupId || !scopeId || !roleId) {
        return {};
    }
    const key = bindingKey(stub);
    const bindings = api.collectionFor(stub.collection);
    const exists = bindings.items.some((item) => item.usergroupId === groupId
        && item.resources[0][key] === scopeId
        && item.permissions[0]['internal:bxp:roleId'] === roleId);
    if (!exists) {
        bindings.items.push({
            usergroupId: groupId,
            resources: [{ [key]: scopeId }],
            permissions: [{ 'internal:bxp:roleId': roleId }],
        });
    }
    return {};
});

registerMutation('unbind', (stub, request, _template, api) => {
    const groupId = request?.params?.[stub.idParam];
    const scopeId = request?.params?.[stub.scopeIdParam];
    const roleId = request?.params?.[stub.roleIdParam];
    const key = bindingKey(stub);
    const bindings = api.collectionFor(stub.collection);
    bindings.items = bindings.items.filter((item) => !(item.usergroupId === groupId
        && item.resources[0][key] === scopeId
        && item.permissions[0]['internal:bxp:roleId'] === roleId));
    return new Response(204);
});

registerMutation('bindings', (stub, request, _template, api) => {
    const groupId = request?.params?.[stub.idParam];
    const items = api.collectionFor(stub.collection).items
        .filter((item) => item.usergroupId === groupId)
        .map(({ usergroupId: _groupId, ...binding }) => _.cloneDeep(binding));
    return { items };
});
